// What the site remembers per browser: sets of keys (which update kinds and
// evenings a game page shows, whether it shades sales; toggles.ts keeps
// them) and single values (the theme picked, the compare chart's scale).
// Storage can be off or full, and reading or writing it can throw (where
// it's off, even looking up localStorage does, so it's looked up where
// that's caught); then what's picked is just this page's. Each function
// takes the storage last, localStorage unless a stand-in is given, so they
// run (and are tested, ../prefs.test.ts) without a browser.

// What's remembered is kept in: localStorage, or a stand-in.
export type PrefStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

// The storage, looked up on each use.
export type GetStorage = () => PrefStorage;

const local: GetStorage = () => localStorage;

// A set of keys remembered under `key`: what was saved, less any that
// `allowed` (an object keyed by them) doesn't have, or `fallback` when
// nothing readable was saved.
export function loadSet(
  key: string,
  allowed: object,
  fallback: Iterable<string>,
  storage = local
): Set<string> {
  try {
    const saved: unknown = JSON.parse(storage().getItem(key) ?? "null");
    if (Array.isArray(saved)) {
      return new Set(
        saved.filter((item): item is string => typeof item === "string" && Object.hasOwn(allowed, item))
      );
    }
  } catch {}
  return new Set(fallback);
}

export function saveSet(key: string, set: ReadonlySet<string>, storage = local): void {
  try {
    storage().setItem(key, JSON.stringify([...set]));
  } catch {}
}

// A remembered set, kept under its key: loaded as loadSet loads it, and
// saved again by each toggle or save().
export class RememberedSet extends Set<string> {
  readonly #key: string;
  readonly #storage: GetStorage;

  constructor(key: string, allowed: object, fallback: Iterable<string>, storage = local) {
    super(loadSet(key, allowed, fallback, storage));
    this.#key = key;
    this.#storage = storage;
  }

  // Turns `item` on or off, and remembers it; whether it's now on.
  toggle(item: string): boolean {
    if (!this.delete(item)) this.add(item);
    this.save();
    return this.has(item);
  }

  save(): void {
    saveSet(this.#key, this, this.#storage);
  }
}

// A value remembered under `key`, if it's one of `allowed`; null for none.
export function loadValue<T extends string>(key: string, allowed: readonly T[], storage = local): T | null {
  try {
    const saved = storage().getItem(key);
    return allowed.find((value) => value === saved) ?? null;
  } catch {
    return null;
  }
}

// Remembers `value` under `key`; null forgets it.
export function saveValue(key: string, value: string | null, storage = local): void {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {}
}
